import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { List, Row, Typography, Space, message, Col } from 'antd';
import Icon, { InfoCircleOutlined, PlusCircleFilled, SyncOutlined } from '@ant-design/icons';
import { userSelector } from '../../redux/selectors/user';
import Page from '../../components/Page/Page';
import Title from '../../components/Page/Title';
import Paper from '../../components/Paper/Paper';
import { PaymentMethod, PaymentType } from '../../api/models/payments';
import PaymentMethodIcon from '../../components/PaymentMethodIcon/PaymentMethodIcon';
import { getDisplayUserBalance } from '../../utils/numbers';
import WalletIcon from '../../assets/img/walletIcon.svg?react';
import Button from '../../components/Button/Button';
import { WALLET_PAYMENT_METHOD_ID } from '../../utils/constants';
import { CardTagDefault, CardTagSecondary } from '../../components/CardTag/CardTag';
import { AppDispatch } from '../../types/store';
import Banner from '../../components/Banner/Banner';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { paymentsScreenOpen, savePaySettingsClicked, topUpClicked } from '../../utils/analytika/events';
import { colorBorder, colorSuccessBg } from '../../styles/theme';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { fetchUserWalletInvoice } from '../../api/payments';
import { getUserIdFromToken } from '../../utils/auth';
import styles from './PaymentsV2.module.less';

const PaymentsV2 = () => {
  const { t } = useTranslation();
  const {
    paymentMethods,
    defaultPaymentMethodId,
    secondaryPaymentMethodId,
    userWalletConfig: { userBalance },
    merchantConfig: {
      availablePaymentMethods: { isWalletEnabled }
    }
  } = useSelector(userSelector);

  const { xl: isXlBreakpoint, xs: isXsBreakpoint, md: isMdBreakpoint } = useBreakpoint();

  const {
    user: {
      updateBothPaymentMethodsUDS,
      publishAnalytikaEvent,
      fetchUserWalletConfig,
      fetchPaymentMethods,
      fetchUserGalileoVars
    },
    modals: { toggleIsAddAmountVisible }
  } = useDispatch<AppDispatch>();

  useEffect(() => {
    const refreshPaymentMethods = async () => {
      await fetchUserGalileoVars(getUserIdFromToken());
      await fetchPaymentMethods();
    };
    refreshPaymentMethods();
  }, []);

  useEffect(() => {
    setDefaultTempId(defaultPaymentMethodId);
    setSecondaryTempId(secondaryPaymentMethodId);
  }, [defaultPaymentMethodId, secondaryPaymentMethodId]);

  const [defaultTempId, setDefaultTempId] = useState(defaultPaymentMethodId);
  const [secondaryTempId, setSecondaryTempId] = useState(secondaryPaymentMethodId);
  const [hasPendingWalletInvoice, setHasPendingWalletInvoice] = useState(false);
  const [isRefreshCoolingDown, setIsRefreshCoolingDown] = useState(false);
  const refreshCooldownTimeoutRef = useRef<number | NodeJS.Timeout | null>(null);

  useEffect(() => {
    publishAnalytikaEvent(
      paymentsScreenOpen({
        primary_payment_method: defaultPaymentMethodId,
        secondary_payment_method: secondaryPaymentMethodId,
        payment_methods_count: isWalletEnabled ? paymentMethods.length + 1 : paymentMethods.length,
        currency: userBalance.currency,
        wallet_balance: userBalance.amount
      })
    );
  }, []);

  useEffect(() => {
    const checkPendingWalletInvoice = () => {
      const pendingInvoiceId = sessionStorage.getItem('wallet_invoice_id');
      const hasPending = Boolean(pendingInvoiceId);
      setHasPendingWalletInvoice(hasPending);

      if (pendingInvoiceId) {
        fetchUserWalletInvoice(pendingInvoiceId)
          .then(response => {
            if (response.status === 'Paid') {
              sessionStorage.removeItem('wallet_invoice_id');
              setHasPendingWalletInvoice(false);
              message.success('Your transaction is successful');
              fetchUserWalletConfig();
            } else if (response.status === 'Failure') {
              sessionStorage.removeItem('wallet_invoice_id');
              setHasPendingWalletInvoice(false);
              const errorMessage = response.errors?.[0]?.message || 'Your transaction has failed';
              message.error(errorMessage);
            }
            // For any other status, keep showing the banner and retain the key
          })
          .catch(() => {
            // On error, keep showing the banner and retain the key
          });
      }
    };

    checkPendingWalletInvoice();
    window.addEventListener('wallet-invoice-updated', checkPendingWalletInvoice);
    return () => window.removeEventListener('wallet-invoice-updated', checkPendingWalletInvoice);
  }, []);

  const refreshPendingWalletInvoice = () => {
    if (isRefreshCoolingDown) return;
    message.info('Refreshing transaction status');
    window.dispatchEvent(new Event('wallet-invoice-updated'));
    setIsRefreshCoolingDown(true);
    if (refreshCooldownTimeoutRef.current) {
      clearTimeout(refreshCooldownTimeoutRef.current as number);
    }
    refreshCooldownTimeoutRef.current = setTimeout(() => {
      setIsRefreshCoolingDown(false);
      refreshCooldownTimeoutRef.current = null;
    }, 30000);
  };

  useEffect(() => {
    return () => {
      if (refreshCooldownTimeoutRef.current) {
        clearTimeout(refreshCooldownTimeoutRef.current as number);
        refreshCooldownTimeoutRef.current = null;
      }
    };
  }, []);

  const mutatedPaymentMethods = useMemo(() => {
    const resultMethods = [...paymentMethods];
    isWalletEnabled &&
      resultMethods.unshift({ paymentType: PaymentType.WALLET, id: WALLET_PAYMENT_METHOD_ID, display: '' });
    return resultMethods;
  }, [paymentMethods, isWalletEnabled, defaultPaymentMethodId, secondaryPaymentMethodId]);

  const handleDiscardTempIds = () => {
    setDefaultTempId(defaultPaymentMethodId);
    setSecondaryTempId(secondaryPaymentMethodId);
  };

  const handleSave = async () => {
    try {
      publishAnalytikaEvent(
        savePaySettingsClicked({ primary_payment_method: defaultTempId, secondary_payment_method: secondaryTempId })
      );
      publishAV2Event({
        event_name: ANALYTIKA_EVENTS_V2.EXPR_SAVE_PAYMENT_SETTING,
        event_version: 2,
        value: `primary: ${defaultTempId}, secondary ${secondaryTempId}`
      });
      await updateBothPaymentMethodsUDS({ primaryMethodId: defaultTempId, secondaryMethodId: secondaryTempId });
      message.success({
        content: t('payments.onSave')
      });
    } catch (err) {
      message.error({
        content: t('payments.onFailure')
      });
    }
  };

  const topUpClickHandle = () => {
    publishAnalytikaEvent(
      topUpClicked({
        wallet_balance: userBalance.currency,
        default_payment_method_flag: defaultTempId === WALLET_PAYMENT_METHOD_ID
      })
    );
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_TOPUP_WALLET,
      event_version: 1,
      wallet_balance: userBalance?.amount,
      wallet_currency: userBalance.currency
    });
    toggleIsAddAmountVisible();
  };

  const isSubmitBlocked = defaultTempId === defaultPaymentMethodId && secondaryTempId === secondaryPaymentMethodId;

  return (
    <Page>
      {isMdBreakpoint && <Title text={t('payments.pageTitle')} />}
      {hasPendingWalletInvoice && (
        <Banner
          type="warning"
          showIcon
          className={styles.pendingTransactionBanner}
          icon={<InfoCircleOutlined />}
          action={
            <Button
              type="text"
              aria-label="Refresh transaction status"
              icon={<SyncOutlined />}
              loading={isRefreshCoolingDown}
              onClick={refreshPendingWalletInvoice}
              disabled={isRefreshCoolingDown}
            />
          }
          message={t('payments.transactionInProcess')}
        />
      )}
      <Paper>
        <Typography.Title type="secondary" level={4}>
          {t(`payments.pageSubTitle`)}
        </Typography.Title>
        <Row style={{ flexFlow: isXlBreakpoint ? 'row' : 'column-reverse' }} gutter={30}>
          <Col xl={14} lg={24}>
            <List
              dataSource={mutatedPaymentMethods}
              itemLayout={isXsBreakpoint ? 'vertical' : 'horizontal'}
              renderItem={(paymentItem: PaymentMethod) => {
                const { id, display, cardType, paymentType, expiryDate, isEnabled } = paymentItem;
                const isDefault = defaultTempId === id;
                const isSecondary = secondaryTempId === id;
                let title;
                let description;
                // @ts-ignore
                let icon = <PaymentMethodIcon style={{ fontSize: 30 }} cardType={cardType} />;
                const isDisabled = isEnabled === false;
                const actions = [];
                !isDefault &&
                  !isDisabled &&
                  actions.push(
                    <Button type="link" key={`${id}-setdefault`} size={'large'} onClick={() => setDefaultTempId(id)}>
                      {t('payments.makeDefault')}
                    </Button>
                  );
                switch (paymentType) {
                  case PaymentType.WALLET:
                    description = t('payments.wallet');
                    title = t('payments.yourBalance', { balance: getDisplayUserBalance(userBalance) });
                    icon = <Icon component={WalletIcon} style={{ fontSize: 30 }} />;
                    actions.push(
                      <Button
                        icon={<PlusCircleFilled />}
                        size="middle"
                        type="primary"
                        key={`wallet-btn-top-up`}
                        onClick={topUpClickHandle}
                      >
                        {t('payments.topUp')}
                      </Button>
                    );
                    break;
                  case PaymentType.CREDIT_CARD:
                    description = t('payments.expiresOn', { expiresOn: expiryDate });
                    title = display;
                    !isSecondary &&
                      !isDisabled &&
                      actions.push(
                        <Button
                          hidden={isSecondary}
                          type="link"
                          size={'large'}
                          key={`${id}-setSecondary`}
                          onClick={() => setSecondaryTempId(id)}
                        >
                          {t('payments.makeSecondary')}
                        </Button>
                      );
                    break;
                  case PaymentType.INVOICE:
                  case PaymentType.CORPORATE_INVOICE:
                    description = t('payments.invoice');
                    title = display;
                    break;
                }

                return (
                  <List.Item
                    style={{
                      background: isDefault ? colorSuccessBg : undefined,
                      opacity: isDisabled ? 0.5 : undefined
                    }}
                    actions={actions}
                  >
                    <List.Item.Meta
                      // @ts-ignore
                      avatar={icon}
                      title={<Typography.Title level={4}>{title}</Typography.Title>}
                      description={
                        <>
                          <Row>
                            <Typography.Text type="secondary">{description}</Typography.Text>
                          </Row>
                          <Row style={{ marginTop: 5 }}>
                            <Space>
                              {isDefault && <CardTagDefault />}
                              {isSecondary && <CardTagSecondary />}
                            </Space>
                          </Row>
                        </>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Col>
          <Col style={{ margin: isXlBreakpoint ? 'unset' : '20px 0' }} xl={10} lg={24}>
            <Banner
              type="info"
              showIcon
              style={{ borderColor: colorBorder }}
              icon={<InfoCircleOutlined />}
              message={t('payments.banner')}
            />
          </Col>
        </Row>

        <Space style={{ marginTop: 20 }} size={'large'}>
          <Button disabled={isSubmitBlocked} size={'large'} type={'default'} onClick={handleDiscardTempIds}>
            {t('payments.discard')}
          </Button>
          <Button disabled={isSubmitBlocked} size={'large'} type={'primary'} onClick={handleSave}>
            {t('payments.save')}
          </Button>
        </Space>
      </Paper>
    </Page>
  );
};

export default PaymentsV2;
